import LineChartBase from './LineChartBase';
import {useEffect, useState, useRef, useCallback} from "react";
import {graphFetch} from "../../api/fetchConfig";
import PopupChart from "./popupLineChart.jsx";
import { Box, CardContent, Fade, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { useDomain, DOMAINS } from '../../contexts/DomainContext';
import { useTabs } from '../../contexts/TabContext';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PowerIcon from '@mui/icons-material/Power';
import DashboardTitle from '../Common/DashboardTitle';

const MAX_DATA_LENGTH = 120;
const MAX_SSE_RETRIES = 3;

const IOTChart = () => {
    const [data, setData] = useState([]);
    const [lines, setLines] = useState([]);
    const [popupOpen, setPopupOpen] = useState(false);
    const [visible, setVisible] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [sseConnected, setSseConnected] = useState(false);

    const eventSourceRef = useRef(null);
    const retryCountRef = useRef(0);

    const theme = useTheme();
    const { domain } = useDomain();
    const { activeTab } = useTabs();
    const isDarkMode = theme.palette.mode === 'dark';
    const isTabActive = activeTab === 'main';

    const processData = useCallback((result) => {
        if (result && result.length > 0) {
            const deviceIds = new Set();
            const newDataPoint = {};

            newDataPoint.timeLabel = result[0].timeLabel.replace("T", " ").replace("Z", " ");

            result.forEach(item => {
                deviceIds.add(item.label);
                const raw = parseFloat(item.value);
                newDataPoint[item.label] = Number(raw.toFixed(2));
            });

            setData(prev => {
                const updated = [...prev, newDataPoint];
                if (updated.length > MAX_DATA_LENGTH) {
                    updated.shift();
                }
                return updated;
            });

            const availableColors = ["blue", "deeppink", "green", "orange", "purple", "red"];
            const newLines = Array.from(deviceIds).map((deviceName, idx) => ({
                key: deviceName,
                color: availableColors[idx % availableColors.length]
            }));
            setLines(newLines);
        }
    }, []);

    const fetchPowerData = useCallback(async () => {
        const query = `
          query getPowerData {
            getPowerData {
              timeLabel
              label
              value
            }
          }
        `;

        try {
            const data = await graphFetch(query);
            if (data?.getPowerData) {
                processData(data.getPowerData);
            }
        } catch (err) {
            console.error('IOT 데이터 fetch 실패:', err);
        }
    }, [processData]);

    const disconnectSSE = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
            setSseConnected(false);
        }
        retryCountRef.current = 0;
    }, []);

    const connectSSE = useCallback(() => {
        if (eventSourceRef.current) {
            return;
        }

        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const sseUrl = isLocalhost ? 'http://localhost:8080/api/sse/power' : '/api/sse/power';

        const eventSource = new EventSource(sseUrl, { withCredentials: true });
        eventSourceRef.current = eventSource;

        eventSource.addEventListener('power-data', (event) => {
            try {
                const result = JSON.parse(event.data);
                processData(result);
            } catch (err) {
                console.error('SSE 메시지 파싱 오류:', err);
            }
        });

        eventSource.onopen = () => {
            retryCountRef.current = 0;
            setSseConnected(true);
        };

        eventSource.onerror = () => {
            setSseConnected(false);
            retryCountRef.current++;
            if (retryCountRef.current >= MAX_SSE_RETRIES) {
                eventSource.close();
                eventSourceRef.current = null;
            }
        };
    }, [processData]);

    useEffect(() => {
        setVisible(true);
    }, []);

    useEffect(() => {
        if (isTabActive) {
            fetchPowerData().then(() => {
                connectSSE();
            });
        } else {
            disconnectSSE();
        }

        return () => {
            disconnectSSE();
        };
    }, [isTabActive, connectSSE, disconnectSSE, fetchPowerData]);


    const handleRefresh = () => {
        setRefreshing(true);
        fetchPowerData();
        setTimeout(() => setRefreshing(false), 1500);
    };
    
    const getTextColor = () => {
        if (domain === DOMAINS.PEMS) {
            return isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.87)';
        }
        return isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.87)';
    };

    const handleDoubleClick = (e) => {
        setPopupOpen(true);
    };

    return (
        <Fade in={visible} timeout={1000}>
            <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                pb: 3,
                '&:last-child': { pb: 3 }
            }}>
                <DashboardTitle
                    icon={PowerIcon}
                    title="IOT 전력량"
                    subtitle={<span>설비 전력 실시간 모니터링 데이터입니다.
                        그래프 영역에 마우스를 올리고, <strong style={{color: '#1976d2'}}>더블클릭시 상세보기</strong>
                        가 가능합니다.</span>}
                    size="small"
                    actions={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="데이터 새로고침">
                                <IconButton
                                    size="small"
                                    onClick={handleRefresh}
                                    sx={{
                                        color: getTextColor(),
                                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                        '&:hover': {
                                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                                            transform: 'rotate(180deg)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <RefreshIcon 
                                        sx={{
                                            transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)',
                                            transition: 'transform 1.5s ease'
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="전체화면으로 보기">
                                <IconButton
                                    size="small"
                                    onClick={handleDoubleClick}
                                    sx={{
                                        color: getTextColor(),
                                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                        '&:hover': {
                                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
                                            transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <FullscreenIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                />
                
                <Box 
                    onDoubleClick={handleDoubleClick}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    sx={{
                        flex: 1,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        position: 'relative',
                        minHeight: 0,
                        border: isHovered ? '2px solid #1976d2' : '2px solid transparent',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                        },
                        '&:before': isHovered ? {
                            content: '"더블클릭하여 상세보기"',
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 10,
                            backgroundColor: 'rgba(25, 118, 210, 0.9)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: 1,
                            fontSize: '12px',
                            fontWeight: 'bold'
                        } : {}
                    }}
                >
                    <Box sx={{ 
                        width: '100%', 
                        height: '100%',
                        minHeight: 0,
                        display: 'flex',
                        alignItems: 'stretch'
                    }}>
                        <LineChartBase data={data}
                                       lines={lines}
                                       xAxisLabel="시간(10s)"
                                       yAxisLabel="전력 (W)"
                        />
                    </Box>
                </Box>

                <PopupChart
                    open={popupOpen}
                    onClose={() => setPopupOpen(false)}
                />
            </CardContent>
        </Fade>
    );
};

export default IOTChart;
